import { Injectable } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';
import { Database } from '../models/database.types';

/** Typed Supabase client for data access, edge functions, and storage. */
@Injectable({ providedIn: 'root' })
export class SupabaseService {
  readonly client: SupabaseClient<Database>;

  constructor() {
    this.client = createClient<Database>(
      environment.supabaseUrl,
      environment.supabaseAnonKey,
      {
        auth: {
          storage: localStorage,
          persistSession: true,
          autoRefreshToken: true,
        },
      },
    );
  }

  /** Accesses a public Supabase table by name. */
  from<T extends keyof Database['public']['Tables']>(table: T) {
    return this.client.from(table);
  }

  /** Invokes a Supabase edge function with a JSON body. */
  async invoke<T>(functionName: string, body: Record<string, unknown> = {}): Promise<T> {
    const { data, error } = await this.client.functions.invoke<T>(functionName, { body });
    if (error) throw error;
    return data as T;
  }

  /** Invokes a Supabase edge function with FormData. */
  async invokeForm<T>(functionName: string, formData: FormData): Promise<T> {
    const { data, error } = await this.client.functions.invoke<T>(functionName, { body: formData });
    if (error) throw error;
    return data as T;
  }

  /** Accesses a Supabase storage bucket. */
  storage(bucket: string) {
    return this.client.storage.from(bucket);
  }

  /** Access to the Supabase authentication module. */
  get auth() {
    return this.client.auth;
  }

  /**
   * Calls a PostgreSQL RPC function exposed by Supabase.
   * @param fn - Name of the allowed RPC function
   * @param args - Named arguments passed to the function
   * @returns Supabase RPC builder resolving to the function result
   */
  rpc(
    fn:
      | 'has_role'
      | 'get_assignable_user_ids'
      | 'set_user_role'
      | 'save_sellsy_flux_mappings'
      | 'outlook_last_synced_at',
    args?: Record<string, unknown>,
  ) {
    return this.client.rpc(fn, args as never);
  }
}
